import { React, useContext, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall, simpleDeleteCall } from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { PageHead } from "./ffUi";
import {
  toDateInput,
  toDateDisplay,
  todayInput,
  useVehicles,
  VehicleSelect,
  vehicleNameById,
  ConfirmDialog,
} from "./ffHelpers";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const money = (n) =>
  n || n === 0 ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "--";

// pick a value out of a row trying several possible key names
const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "")
      return obj[k];
  }
  return null;
};

// U-07: a schedule row may carry the period's date; show it as "Feb 2024".
const PERIOD_DATE_KEYS = [
  "period_date",
  "period_end",
  "period_end_date",
  "period_start",
  "period_start_date",
  "period_month",
  "month",
  "date",
];
const periodMonth = (row) => {
  const raw = pick(row, PERIOD_DATE_KEYS);
  const ymd = toDateInput(raw);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  const [y, m] = ymd.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, {
    month: "short",
    year: "numeric",
  });
};

const Depreciation = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const vehicles = useVehicles(); // U-01
  const [vehicleId, setVehicleId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(""); // which action is running
  const [scheduleLabel, setScheduleLabel] = useState("");

  // run modal (explicit posting with as_of)
  const [showRun, setShowRun] = useState(false);
  const [asOf, setAsOf] = useState(todayInput()); // FF-06
  const [running, setRunning] = useState(false);

  // FF-15 / U-08: confirm before clearing unposted periods
  const [showReset, setShowReset] = useState(false);

  // dtl / report modals
  const [showDtl, setShowDtl] = useState(false);
  const [dtl, setDtl] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState(null);

  // U-02: show vehicle name/number instead of the raw id (never "10.00")
  const vehicleLabel = (row) =>
    row?.vehicle_name || row?.vehicle_number || vehicleNameById(vehicles, row?.vehicle_id);

  const requireVehicle = () => {
    if (!vehicleId) {
      notifyError("Select a vehicle first");
      return false;
    }
    return true;
  };

  const loadSchedule = (url, label, action) => {
    if (!requireVehicle()) return;
    setBusy(action);
    setLoading(true);
    simplePostCall(url, JSON.stringify({ vehicle_id: Number(vehicleId) }))
      .then((data) => {
        if (data?.success || data?.result) {
          // preview returns rows top-level as `schedule`; the stored
          // `schedule` endpoint nests them under `data`. (generate returns
          // only a summary with no rows, so it re-fetches the stored schedule.)
          const list =
            [
              data?.schedule,
              data?.data?.schedule,
              data?.data?.periods,
              data?.data,
            ].find((x) => Array.isArray(x)) || [];
          setRows(list);
          setScheduleLabel(label);
          if (action !== "preview" && action !== "generate")
            notifySuccess(data?.message || label);
        } else {
          setRows([]);
          notifyError(data?.message || data?.error || "Unable to load schedule");
        }
      })
      .catch((err) => {
        console.log("depreciation err", err);
        notifyError("Something went wrong");
      })
      .finally(() => {
        setBusy("");
        setLoading(false);
      });
  };

  const preview = () =>
    loadSchedule(ApiConfig.FLEET_FINANCE_DEPRECIATION_PREVIEW, t("Preview (not stored)"), "preview");
  const generate = () => {
    if (!requireVehicle()) return;
    setBusy("generate");
    setLoading(true);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_DEPRECIATION_GENERATE,
      JSON.stringify({ vehicle_id: Number(vehicleId) })
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Schedule generated & stored");
          // generate returns only a summary (no rows) -> load the stored schedule to display
          loadSchedule(
            ApiConfig.FLEET_FINANCE_DEPRECIATION_SCHEDULE,
            t("Schedule generated & stored"),
            "generate"
          );
        } else {
          notifyError(data?.message || data?.error || "Unable to generate schedule");
          setBusy("");
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log("generate err", err);
        notifyError("Something went wrong");
        setBusy("");
        setLoading(false);
      });
  };
  const getStored = () =>
    loadSchedule(ApiConfig.FLEET_FINANCE_DEPRECIATION_SCHEDULE, t("Stored schedule"), "schedule");

  const runDepreciation = () => {
    if (!requireVehicle()) return;
    setRunning(true);
    const body = JSON.stringify({ vehicle_id: Number(vehicleId), as_of: asOf });
    simplePostCall(ApiConfig.FLEET_FINANCE_DEPRECIATION_RUN, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Depreciation posted to ledger");
          setShowRun(false);
          getStored();
        } else {
          notifyError(data?.message || data?.error || "Unable to post depreciation");
        }
      })
      .catch((err) => {
        console.log("run depreciation err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setRunning(false));
  };

  const askReset = () => {
    if (!requireVehicle()) return;
    setShowReset(true); // FF-15 / U-08
  };

  const resetSchedule = () => {
    if (!requireVehicle()) return;
    setShowReset(false);
    setBusy("reset");
    simpleDeleteCall(
      ApiConfig.FLEET_FINANCE_DEPRECIATION_DELETE,
      JSON.stringify({ vehicle_id: Number(vehicleId) })
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Unposted schedule cleared");
          setRows([]);
          setScheduleLabel("");
        } else {
          notifyError(data?.message || data?.error || "Unable to reset schedule");
        }
      })
      .catch((err) => {
        console.log("reset depreciation err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setBusy(""));
  };

  const openDtl = () => {
    setShowDtl(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_DEPRECIATION_DTL, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result) setDtl(data?.data || []);
        else setDtl([]);
      })
      .catch((err) => {
        console.log("dtl err", err);
        setDtl([]);
      });
  };

  const openReport = () => {
    setShowReport(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_DEPRECIATION_REPORT, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result) setReport(data?.data || data?.report || {});
        else setReport(null);
      })
      .catch((err) => {
        console.log("depreciation report err", err);
        setReport(null);
      });
  };

  const anyBusy = !!busy;

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Depreciation">
        <PageHead
          title={t("Depreciation & Tax")}
          subtitle={t("IFRS book vs tax schedules, deferred tax & ledger posting")}
        />
        <div className="main-master-wrapper">
          <div className="row align-items-end" style={{ marginBottom: 12 }}>
            <div className="col-md-3 form_input_main">
              <Form.Label className="common-labels">{t("Vehicle")}</Form.Label>
              {/* U-01 */}
              <VehicleSelect
                vehicles={vehicles}
                value={vehicleId}
                onChange={(e) => {
                  setVehicleId(e.target.value);
                  setRows([]);
                  setScheduleLabel("");
                }}
              />
            </div>
            <div className="col-md-9 d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
              <button className="cx-btn-1" onClick={preview} disabled={anyBusy}>
                {busy === "preview" ? <Spinner animation="border" size="sm" /> : t("Preview")}
              </button>
              <button className="cx-btn-1" onClick={generate} disabled={anyBusy}>
                {busy === "generate" ? <Spinner animation="border" size="sm" /> : t("Generate")}
              </button>
              <button className="cx-btn-1" onClick={getStored} disabled={anyBusy}>
                {busy === "schedule" ? <Spinner animation="border" size="sm" /> : t("Get Schedule")}
              </button>
              <button
                className="cx-btn-2"
                onClick={() => {
                  if (requireVehicle()) {
                    setAsOf(todayInput());
                    setShowRun(true);
                  }
                }}
                disabled={anyBusy}
              >
                {t("Post to Ledger")}
              </button>
              <button className="cx-btn-1" onClick={askReset} disabled={anyBusy}>
                {busy === "reset" ? <Spinner animation="border" size="sm" /> : t("Reset Unposted")}
              </button>
              <button className="cx-btn-1" onClick={openDtl}>
                {t("DTL")}
              </button>
              <button className="cx-btn-1" onClick={openReport}>
                {t("Fleet Report")}
              </button>
            </div>
          </div>

          <p style={{ color: "#8f4300", fontSize: 13, marginBottom: 8 }}>
            {t(
              "Depreciation needs the TCO config (acquisition cost, residual, useful life, in-service date). 'Generate' only builds a schedule — nothing posts until you click 'Post to Ledger'."
            )}
          </p>

          {scheduleLabel && (
            <p style={{ color: "#703c19", fontWeight: 600, marginBottom: 6 }}>
              {scheduleLabel}
            </p>
          )}

          {loading ? (
            <Loader />
          ) : (
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Period")}</th>
                      <th>{t("Book Depr.")}</th>
                      <th>{t("Accum. Book")}</th>
                      <th>{t("Book NBV")}</th>
                      <th>{t("Tax Depr.")}</th>
                      <th>{t("Accum. Tax")}</th>
                      <th>{t("DTL")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {rows && rows.length > 0 ? (
                      rows.map((r, i) => {
                        // U-07: "Feb 2024 #2" when the row carries a date, else "Period 2"
                        const n = pick(r, ["period_no", "period", "period_index"]) || i + 1;
                        const month = periodMonth(r);
                        return (
                        <tr key={r.period || r.period_no || i}>
                          <td>
                            {month ? (
                              <>
                                {month}{" "}
                                <small style={{ opacity: 0.6 }}>#{n}</small>
                              </>
                            ) : (
                              `${t("Period")} ${n}`
                            )}
                          </td>
                          <td>{money(pick(r, ["book_depreciation", "book_depr", "depreciation"]))}</td>
                          <td>{money(pick(r, ["book_accumulated", "accumulated_book", "accum_book"]))}</td>
                          <td>{money(pick(r, ["book_nbv", "nbv", "net_book_value"]))}</td>
                          <td>{money(pick(r, ["tax_depreciation", "tax_depr"]))}</td>
                          <td>{money(pick(r, ["tax_accumulated", "accumulated_tax", "accum_tax"]))}</td>
                          <td>{money(pick(r, ["dtl_amount", "dtl", "deferred_tax", "deferred_tax_liability"]))}</td>
                        </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Run / Post modal */}
      <Modal show={showRun} onHide={() => setShowRun(false)} centered className="common-model">
        <Modal.Header closeButton>
          <Modal.Title>{t("Post Depreciation to Ledger")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: "#703c19" }}>
            {t(
              "Posts Dr 7010 / Cr 1512 for every due, unposted period up to the date below. Each period posts once."
            )}
          </p>
          <div className="form_input_main">
            <Form.Label className="common-labels">{t("As of date")}</Form.Label>
            <Form.Control
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="pop-up-modal-footer">
          <div className="btn-wrapper">
            <button type="button" className="cx-btn-1" onClick={() => setShowRun(false)}>
              {t("Cancel")}
            </button>
            <button className="cx-btn-2" onClick={runDepreciation} disabled={running}>
              {running ? <Spinner animation="border" size="sm" /> : t("Post")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* FF-15 / U-08: Reset confirmation */}
      <ConfirmDialog
        show={showReset}
        title={t("Reset unposted periods")}
        body={t(
          "This deletes every unposted schedule row for this vehicle. Posted periods are kept. Continue?"
        )}
        confirmText={t("Yes, reset")}
        cancelText={t("Cancel")}
        onConfirm={resetSchedule}
        onCancel={() => setShowReset(false)}
        busy={busy === "reset"}
      />

      {/* DTL modal */}
      <Modal show={showDtl} onHide={() => setShowDtl(false)} centered size="lg" className="common-model ff-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("Deferred Tax Liability (per vehicle)")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="VA_table">
            <table className="table tableAdmin">
              <thead className="tableHead">
                <tr>
                  <th>{t("Vehicle")}</th>
                  <th>{t("Accum. Book")}</th>
                  <th>{t("Accum. Tax")}</th>
                  <th>{t("DTL")}</th>
                </tr>
              </thead>
              <tbody className="tableBody">
                {dtl.length > 0 ? (
                  dtl.map((d, i) => (
                    <tr key={d.vehicle_id || i}>
                      <td>{vehicleLabel(d)}</td>{/* U-02 */}
                      <td>{money(pick(d, ["book_accumulated", "accumulated_book"]))}</td>
                      <td>{money(pick(d, ["tax_accumulated", "accumulated_tax"]))}</td>
                      <td>{money(pick(d, ["dtl_amount", "dtl", "deferred_tax_liability"]))}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <NoDataComp />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>

      {/* Fleet report modal */}
      <Modal show={showReport} onHide={() => setShowReport(false)} centered size="lg" className="common-model ff-modal">
        <Modal.Header closeButton>
          <Modal.Title>{report?.title || t("Depreciation Report")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {report ? (
            <>
              <div className="row" style={{ marginBottom: 12 }}>
                <div className="col-md-4">
                  <b>{t("Currency")}:</b> {report.currency || "-"}
                </div>
                <div className="col-md-4">
                  <b>{t("Vehicles")}:</b> {report.vehicle_count ?? "-"}
                </div>
                <div className="col-md-4">
                  <b>{t("As of")}:</b> {toDateDisplay(report.as_of)}{/* FF-06 */}
                </div>
              </div>
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Vehicle")}</th>
                      <th>{t("Accum. Book")}</th>
                      <th>{t("Accum. Tax")}</th>
                      <th>{t("Timing Diff.")}</th>
                      <th>{t("DTL")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {(report.rows || []).length > 0 ? (
                      report.rows.map((r, i) => (
                        <tr key={r.vehicle_id || i}>
                          <td>{vehicleLabel(r)}</td>{/* U-02 */}
                          <td>{money(r.book_accumulated)}</td>
                          <td>{money(r.tax_accumulated)}</td>
                          <td>{money(r.timing_difference)}</td>
                          <td>{money(r.dtl_amount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {report.totals && (
                    <tfoot>
                      <tr style={{ fontWeight: 700 }}>
                        <td>{t("Total")}</td>
                        <td>{money(report.totals.book_accumulated)}</td>
                        <td>{money(report.totals.tax_accumulated)}</td>
                        <td>-</td>
                        <td>{money(report.totals.dtl_amount)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          ) : (
            <NoDataComp />
          )}
        </Modal.Body>
      </Modal>
    </motion.div>
  );
};

export default Depreciation;
